import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Flex,
  Button,
  useColorModeValue,
  IconButton,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  HStack,
  Tooltip,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Divider,
  Container,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  InputGroup,
  InputLeftElement,
  Input,
  useToast,
} from '@chakra-ui/react';
import { HamburgerIcon, EditIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FaFacebook, FaInstagram, FaYoutube, FaPhotoVideo, FaExternalLinkAlt, FaShoppingBag, FaCamera, FaSearch } from 'react-icons/fa';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { useClubTheme } from '../contexts/ClubThemeContext';
import { Image } from '@chakra-ui/react';
import { getCategories, Category } from '../services/public';
import { FaSearch as FaSearchIcon } from 'react-icons/fa';
import { getNavigationItems, NavigationItem, seedDefaultNavigation } from '../services/navigation';
import { getEvents } from '../services/eventService';
import { getPlayers } from '../services/public';
import { getArticles } from '../services/articles';
import { LanguageSwitcher } from './common/LanguageSwitcher';
import { getCachedYouTube } from '../services/youtube';
import { getZoneramaManifestWithFallbacks } from '../services/zonerama';
import { getMyNewsletterToken } from '../services/public/newsletter';
import { API_URL } from '../services/api';
import { assetUrl } from '../utils/url';
import { getProfile as getEngagementProfile, EngagementProfile } from '../services/engagement';
import AchievementsModal from './engagement/AchievementsModal';
import RewardsModal from './engagement/RewardsModal';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useTranslation } from 'react-i18next';
import { useNavbarData } from '../hooks/useNavbarData';

type NavLink = { label: string; to?: string; items?: { label: string; to: string }[]; external?: boolean };

// Minimal normalization for social URLs so admins can input @handle or domain-less usernames
const normalizeSocialUrl = (network: 'facebook' | 'instagram' | 'youtube', raw?: string | null): string | null => {
  let v = String(raw || '').trim();
  if (!v) return null;
  v = v.replace(/\s+/g, '');
  if (v.startsWith('@')) {
    const handle = v.slice(1);
    if (network === 'facebook') return `https://www.facebook.com/${handle}`;
    if (network === 'instagram') return `https://www.instagram.com/${handle}`;
    if (network === 'youtube') return `https://www.youtube.com/@${handle}`;
  }
  if (!/^https?:\/\//i.test(v) && !v.includes('/') && !v.includes('.')) {
    if (network === 'facebook') return `https://www.facebook.com/${v}`;
    if (network === 'instagram') return `https://www.instagram.com/${v}`;
    if (network === 'youtube') return `https://www.youtube.com/@${v}`;
  }
  if (!/^https?:\/\//i.test(v)) {
    if (/^facebook\.com\//i.test(v)) return `https://www.${v}`;
    if (/^instagram\.com\//i.test(v)) return `https://www.${v}`;
    if (/^youtube\.com\//i.test(v)) return `https://www.${v}`;
  }
  return v;
};

// Mobile menu component
const MobileMenu = ({ isOpen, onClose, isAdmin, isAuthenticated, menuBg, dividerColor, settings, categories, galleryHref, galleryLabel, hasTables, hasActivities, hasPlayers, hasArticles, hasVideos, hasGallery, dynamicNavItems, navLoading, t, i18n, handleSimpleLanguageChange }: { 
  isOpen: boolean; 
  onClose: () => void;
  isAdmin: boolean;
  isAuthenticated: boolean;
  menuBg: string;
  dividerColor: string;
  settings?: any;
  categories?: Category[] | null;
  galleryHref?: string | null;
  galleryLabel?: string;
  hasTables?: boolean;
  hasActivities?: boolean;
  hasPlayers?: boolean;
  hasArticles?: boolean;
  hasVideos?: boolean;
  hasGallery?: boolean;
  dynamicNavItems?: NavigationItem[];
  navLoading?: boolean;
  t: (key: string) => string;
  i18n: any;
  handleSimpleLanguageChange: (lang: string) => void;
}) => (
  <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
    <DrawerOverlay />
    <DrawerContent bg={menuBg}>
      <DrawerCloseButton />
      <DrawerHeader borderBottomWidth="1px" borderColor="border.subtle">{t('action.open_menu')}</DrawerHeader>
      <DrawerBody>
    <VStack align="stretch" spacing={2}>
      {/* Dynamic navigation items in mobile */}
      {(!navLoading && dynamicNavItems && dynamicNavItems.length > 0) ? (
        dynamicNavItems.map((item, idx) => {
          const linkIsExternal = item.type === 'external';
          const hasChildren = item.type === 'dropdown' && item.children && item.children.length > 0;
          const linkProps = linkIsExternal ? { href: item.url } : { to: item.url || '/' };
          const Comp: any = linkIsExternal ? 'a' : RouterLink;
          // Special handling for Blog/Články: when categories exist, make parent non-clickable and list categories
          const isArticlesLink = (item.label === 'Články' || item.label === 'Blog' || ((item.url || '') as string).startsWith('/blog'));
          const hasCats = Array.isArray(categories) && categories.length > 0;

          if (isArticlesLink && hasCats) {
            return (
              <React.Fragment key={item.id || idx}>
                <Button
                  as={RouterLink}
                  to="/blog"
                  onClick={onClose}
                  variant="ghost"
                  justifyContent="flex-start"
                  fontWeight="bold"
                >
                  {item.label}
                </Button>
                <VStack align="stretch" pl={4} spacing={1}>
                  {categories.map((cat: any, catIdx: number) => {
                    const catIsExternal = typeof cat.url === 'string' && /^https?:\/\//i.test(cat.url);
                    const catHref = cat.url || (cat.id ? `/blog?category_id=${cat.id}` : (cat.slug ? `/blog?category=${encodeURIComponent(cat.slug)}` : '/blog'));
                    const catLinkProps = catIsExternal ? { href: catHref } : { to: catHref };
                    const CatComp: any = catIsExternal ? 'a' : RouterLink;
                    return (
                      <Button key={cat.slug || cat.id || cat.name} as={CatComp} {...(catLinkProps as any)} onClick={onClose} variant="ghost" justifyContent="flex-start" fontWeight="normal" size="sm">
                        {cat.name}
                      </Button>
                    );
                  })}
                </VStack>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={item.id || idx}>
              <Button
                as={Comp}
                {...linkProps}
                target={linkIsExternal ? '_blank' : undefined}
                rel={linkIsExternal ? 'noreferrer' : undefined}
                variant="ghost"
                justifyContent="flex-start"
                fontWeight={hasChildren ? 'bold' : 'normal'}
              >
                {item.label}
              </Button>
              {/* Render children for dropdown items */}
              {hasChildren && (
                <VStack align="stretch" pl={4} spacing={1}>
                  {item.children!.map((child) => {
                    const childIsExternal = child.type === 'external';
                    const childLinkProps = childIsExternal ? { href: child.url } : { to: child.url || '/' };
                    const ChildComp: any = childIsExternal ? 'a' : RouterLink;
                    return (
                      <Button 
                        key={child.id} 
                        as={ChildComp} 
                        {...(childLinkProps as any)} 
                        variant="ghost" 
                        justifyContent="flex-start" 
                        fontWeight="normal" 
                        size="sm"
                      >
                        {child.label}
                      </Button>
                    );
                  })}
                </VStack>
              )}
            </React.Fragment>
          );
        })
      ) : (
        // Fallback to hardcoded navigation
        <>
          <Button as={RouterLink} to="/" variant="ghost" justifyContent="flex-start">{t('nav.home')}</Button>
          {(settings?.show_about_in_nav ?? true) && (
            <Button as={RouterLink} to="/o-klubu" variant="ghost" justifyContent="flex-start">{t('nav.club')}</Button>
          )}
          <Button as={RouterLink} to="/kalendar" variant="ghost" justifyContent="flex-start">{t('nav.calendar')}</Button>
          <Button as={RouterLink} to="/zapasy" variant="ghost" justifyContent="flex-start">{t('nav.matches')}</Button>
          {hasActivities && (
            <Button as={RouterLink} to="/aktivity" variant="ghost" justifyContent="flex-start">{t('nav.activities')}</Button>
          )}
          {hasPlayers && (
            <Button as={RouterLink} to="/hraci" variant="ghost" justifyContent="flex-start">{t('nav.players')}</Button>
          )}
          {hasTables && (
            <Button as={RouterLink} to="/tabulky" variant="ghost" justifyContent="flex-start">{t('nav.tables')}</Button>
          )}
          {Array.isArray(settings?.custom_nav) && settings.custom_nav.length > 0 && settings.custom_nav.map((item: any, idx: number) => {
            const customLinkIsExternal = typeof item?.url === 'string' && /^https?:\/\//i.test(item.url);
            const linkProps = customLinkIsExternal ? { href: item.url } : { to: item.url || '/' };
            const Comp: any = customLinkIsExternal ? 'a' : RouterLink;
            return (
              <Button
                key={`custom-nav-${idx}-${item?.label || 'link'}`}
                as={Comp}
                {...linkProps}
                target={customLinkIsExternal ? '_blank' : undefined}
                rel={customLinkIsExternal ? 'noreferrer' : undefined}
                variant="ghost"
                justifyContent="flex-start"
              >
                {item?.label || t('common.page')}
              </Button>
            );
          })}
          {hasArticles && (
            <>
              {Array.isArray(categories) && categories.length > 0 ? (
                <>
                  <Button as={RouterLink} to="/blog" onClick={onClose} variant="ghost" justifyContent="flex-start" fontWeight="bold">
                    {t('nav.articles')}
                  </Button>
                  <VStack align="stretch" pl={4} spacing={1}>
                    {categories.map((cat: any, catIdx: number) => {
                      const catIsExternal = typeof cat.url === 'string' && /^https?:\/\//i.test(cat.url);
                      const catHref = cat.url || (cat.id ? `/blog?category_id=${cat.id}` : (cat.slug ? `/blog?category=${encodeURIComponent(cat.slug)}` : '/blog'));
                      const catLinkProps = catIsExternal ? { href: catHref } : { to: catHref };
                      return (
                        <Button key={cat.slug || cat.id || cat.name} as={catIsExternal ? 'a' : RouterLink} {...(catLinkProps as any)} onClick={onClose} variant="ghost" justifyContent="flex-start" fontWeight="normal" size="sm">
                          {cat.name}
                        </Button>
                      );
                    })}
                  </VStack>
                </>
              ) : (
                <Button as={RouterLink} to="/blog" onClick={onClose} variant="ghost" justifyContent="flex-start" fontWeight="bold">
                  {t('nav.articles')}
                </Button>
              )}
            </>
          )}
          {hasVideos && (
            <Button as={RouterLink} to="/videa" variant="ghost" justifyContent="flex-start">{t('nav.videos')}</Button>
          )}
          <Button as={RouterLink} to="/hledat" variant="ghost" justifyContent="flex-start">{t('action.search')}</Button>
          {hasGallery && (
            <Button as={RouterLink} to="/galerie" variant="ghost" justifyContent="flex-start">{galleryLabel || t('homepage.gallery')}</Button>
          )}
          {settings?.shop_url && (
            <Button as="a" href={settings.shop_url} target="_blank" rel="noreferrer" variant="ghost" justifyContent="flex-start">{t('nav.shop')}</Button>
          )}
          <Button as={RouterLink} to="/sponzori" variant="ghost" justifyContent="flex-start">{t('nav.sponsors')}</Button>
          <Button as={RouterLink} to="/kontakt" variant="ghost" justifyContent="flex-start">{t('nav.contact')}</Button>
        </>
      )}
          
      {isAdmin && (
        <>
          <Divider my={2} borderColor={dividerColor} />
          <Text fontWeight="bold" mt={2} color={dividerColor}>{t('nav.admin')}</Text>
          <Button as={RouterLink} to="/admin" variant="ghost" justifyContent="flex-start" colorScheme="blue">
            {t('nav.admin')}
          </Button>
        </>
      )}

      {!isAuthenticated && (
        <>
          <Divider my={2} borderColor={dividerColor} />
          <Button as={RouterLink} to="/login" colorScheme="blue" justifyContent="flex-start">
            {t('action.login')}
          </Button>
          <Button as={RouterLink} to="/register" variant="outline" justifyContent="flex-start">
            {t('common.register')}
          </Button>
        </>
      )}
      
      {/* Language switcher for mobile */}
      <LanguageSwitcher />
    </VStack>
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);

const Navbar: React.FC<{ fullWidth?: boolean; variant?: string }> = ({ fullWidth = false, variant = 'unified' }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout, user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure();
  const isAdmin = user?.role === 'admin';
  const accountPath = isAdmin ? '/admin/nastaveni' : '/semiadmin';
  const { data: settings } = usePublicSettings();
  const theme = useClubTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const menuBg = useColorModeValue('white', '#0f1115');
  const dividerColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const activeBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const activeTextColor = useColorModeValue('brand.primary', 'brand.accent');
  const navTextColor = useColorModeValue('gray.700', 'gray.200');
  const topBarBg = useColorModeValue('gray.50', 'blackAlpha.500');
  const [scrolled, setScrolled] = useState(false);
  const containerMaxW = fullWidth ? 'full' as const : '7xl' as const;
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [engProfile, setEngProfile] = useState<EngagementProfile | null>(null);
  const { isOpen: isAchOpen, onOpen: onAchOpen, onClose: onAchClose } = useDisclosure();
  const { isOpen: isRewOpen, onOpen: onRewOpen, onClose: onRewClose } = useDisclosure();

  // Use the combined navbar data hook
  const navbarData = useNavbarData(isAdmin, settings);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load engagement profile for avatar
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!isAuthenticated) { setEngProfile(null); return; }
        const p = await getEngagementProfile();
        if (mounted) setEngProfile(p);
      } catch {
        if (mounted) setEngProfile(null);
      }
    })();
    return () => { mounted = false; };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let disposed = false;
    const handler = async () => {
      try {
        const p = await getEngagementProfile();
        if (!disposed) setEngProfile(p);
      } catch {}
    };
    window.addEventListener('engagement:refresh', handler as any);
    return () => { disposed = true; window.removeEventListener('engagement:refresh', handler as any); };
  }, [isAuthenticated]);

  // Search modal state
  const [query, setQuery] = useState('');
  const submitSearch = () => {
    const text = query.trim();
    if (!text) return;
    onSearchClose();
    setQuery('');
    navigate(`/hledat?q=${encodeURIComponent(text)}`);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true } as any);
    return () => window.removeEventListener('scroll', onScroll as any);
  }, []);

  // Simple language change handler for non-logged-in users
  const handleSimpleLanguageChange = async (languageCode: string) => {
    try {
      // Change language in i18next
      await i18n.changeLanguage(languageCode);
      
      // Save to localStorage and cookie
      try {
        localStorage.setItem('language', languageCode);
      } catch (e) {
        // Ignore localStorage errors
      }
      document.cookie = `lang=${languageCode}; max-age=${365 * 24 * 60 * 60}; path=/`;
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  // Open newsletter preferences for logged-in user (fetch token and redirect)
  const openMyNewsletterPrefs = async () => {
    try {
      const { token } = await getMyNewsletterToken();
      navigate(`/newsletter/preferences?token=${encodeURIComponent(token)}`);
    } catch (err: any) {
      toast({
        title: 'Chyba',
        description: 'Nelze načíst odkaz na e‑mailové preference. Zkuste to prosím znovu.',
        status: 'error',
        duration: 4000,
      });
    }
  };

  // Also set document title to club name ASAP (SEO component will refine further)
  useEffect(() => {
    const name = settings?.club_name || theme.name;
    if (name && typeof document !== 'undefined') {
      document.title = name;
    }
  }, [settings?.club_name, theme.name]);

  // Set favicon/logo in head for fan pages (SPA)
  useEffect(() => {
    try {
      const raw = settings?.club_logo_url || theme.logoUrl || '/dist/img/logo-club-empty.svg';
      if (!raw) return;
      const url = assetUrl(raw) || raw;

      const setIcon = (rel: string) => {
        let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
        if (!link) {
          link = document.createElement('link');
          link.rel = rel as any;
          document.head.appendChild(link);
        }
        link.href = url;
        // Try to hint type if svg
        if (url.endsWith('.svg')) link.type = 'image/svg+xml';
      };
      setIcon('icon');
      setIcon('shortcut icon');
    } catch {}
  }, [settings?.club_logo_url, theme.logoUrl]);

  // gallery link (generic first, fallback to zonerama)
  const galleryHref = settings?.gallery_url || settings?.zonerama_url;
  const galleryLabel = settings?.gallery_label || t('homepage.gallery');

  const isPathActive = (to?: string) => {
    if (!to) return false;
    // Active when current pathname starts with target (handles subroutes)
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  // Convert NavigationItem to NavLink format
  const convertToNavLink = (item: NavigationItem): NavLink => {
    // Map known Czech labels to translation keys
    const getTranslatedLabel = (label: string) => {
      const labelMap: Record<string, string> = {
        'Domů': 'nav.home',
        'Aktuality': 'nav.news',
        'Zápasy': 'nav.matches',
        'Hráči': 'nav.players',
        'Fotogalerie': 'nav.gallery',
        'Videa': 'nav.videos',
        'Kontakt': 'nav.contact',
        'O klubu': 'nav.about',
        'Aktivity': 'nav.activities',
        'Sponzoři': 'nav.sponsors',
        'Články': 'nav.news',
        'Blog': 'nav.news',
        'Kalendář': 'nav.calendar',
        'Tabulky': 'nav.table'
      };
      const translationKey = labelMap[label];
      return translationKey ? t(translationKey) : label;
    };

    const link: NavLink = {
      label: getTranslatedLabel(item.label),
      to: item.url || '#',
      external: item.type === 'external',
    };
    
    // Add children for dropdown items
    if (item.type === 'dropdown' && item.children && item.children.length > 0) {
      link.items = item.children.map(child => ({
        label: getTranslatedLabel(child.label),
        to: child.url || '#',
      }));
    }
    
    return link;
  };

  // Build categories as items for Články dropdown (fallback)
  const categoryItems = useMemo(() => {
    const source = Array.isArray(navbarData.categories) && navbarData.categories.length > 0 ? navbarData.categories : [];
    return source.map((cat: any) => ({
      label: cat.name,
      to: cat.url || (cat.id ? `/blog?category_id=${cat.id}` : (cat.slug ? `/blog?category=${encodeURIComponent(cat.slug)}` : '/blog'))
    }));
  }, [navbarData.categories]);

  // Filter dynamic navigation items based on available data (only show when data exists)
  const filteredDynamicNavItems = useMemo(() => {
    const filterItem = (item: NavigationItem): NavigationItem | null => {
      const url = item.url || '';
      if (url.startsWith('/aktivity') && !navbarData.hasActivities) return null;
      if (url.startsWith('/hraci') && !navbarData.hasPlayers) return null;
      if (url.startsWith('/blog') && !navbarData.hasArticles) return null;
      if (url.startsWith('/videa') && !navbarData.hasVideos) return null;
      if (url.startsWith('/galerie') && !navbarData.hasGallery) return null;
      if (item.type === 'dropdown' && Array.isArray(item.children)) {
        const children = item.children.map(filterItem).filter(Boolean) as NavigationItem[];
        return { ...item, children };
      }
      return item;
    };
    return navbarData.dynamicNavItems.map(filterItem).filter(Boolean) as NavigationItem[];
  }, [navbarData.dynamicNavItems, navbarData.hasActivities, navbarData.hasPlayers, navbarData.hasArticles, navbarData.hasVideos, navbarData.hasGallery]);

  // Use dynamic navigation if available, otherwise fallback to hardcoded
  let NAV_LINKS: NavLink[] = useMemo(() => {
    if (!navbarData.navLoading && filteredDynamicNavItems.length > 0) {
      console.log('Navbar: Using dynamic navigation, items:', filteredDynamicNavItems.length);
      // Use dynamic navigation from API
      const navLinks = filteredDynamicNavItems.map(convertToNavLink);
      
      // Inject categories into "Články" or "Blog" navigation item if it exists
      if (categoryItems.length > 0) {
        const articlesIndex = navLinks.findIndex(link => 
          link.label === 'Články' || 
          link.label === 'Blog' || 
          link.to === '/blog'
        );
        
        if (articlesIndex !== -1) {
          // Add or merge categories into the articles navigation item
          navLinks[articlesIndex] = {
            ...navLinks[articlesIndex],
            items: categoryItems
          };
        }
      }

      // Ensure we only show sections when there is data
      const filtered = navLinks.filter((link) => {
        const to = link.to || '';
        if (to.startsWith('/aktivity')) return navbarData.hasActivities;
        if (to.startsWith('/hraci')) return navbarData.hasPlayers;
        if (to.startsWith('/blog')) return navbarData.hasArticles;
        if (to.startsWith('/videa')) return navbarData.hasVideos;
        if (to.startsWith('/galerie')) return navbarData.hasGallery;
        return true;
      });

      return filtered;
    }
    
    console.log('Navbar: Using fallback hardcoded navigation, language:', i18n.language);
    // Fallback to hardcoded navigation - modular based on language
    const currentLanguage = i18n.language;
    let links: NavLink[] = [];
    
    // Base navigation items for all languages
    const baseLinks: NavLink[] = [
      { label: t('nav.home'), to: '/' },
    ];
    
    // Add club info if enabled
    if (settings?.show_about_in_nav !== false) {
      baseLinks.push({ label: t('nav.club'), to: '/o-klubu' });
    }
    
    // Add calendar and matches for all languages
    baseLinks.push(
      { label: t('nav.calendar'), to: '/kalendar' },
      { label: t('nav.matches'), to: '/zapasy' }
    );
    
    // Add optional items only if data exists (for all languages)
    if (navbarData.hasActivities) {
      baseLinks.push({ label: t('nav.activities'), to: '/aktivity' });
    }
    if (navbarData.hasPlayers) {
      baseLinks.push({ label: t('nav.players'), to: '/hraci' });
    }
    if (navbarData.hasTables) {
      baseLinks.push({ label: t('nav.tables'), to: '/tabulky' });
    }
    if (navbarData.hasArticles) {
      baseLinks.push(
        categoryItems.length > 0 
          ? { label: t('nav.articles'), to: '/blog', items: categoryItems } 
          : { label: t('nav.articles'), to: '/blog' }
      );
    }
    if (navbarData.hasVideos) {
      baseLinks.push({ label: t('nav.videos'), to: '/videa' });
    }
    if (navbarData.hasGallery) {
      baseLinks.push({ label: galleryLabel, to: '/galerie' });
    }
    
    // Always show sponsors and contact
    baseLinks.push(
      { label: t('nav.sponsors'), to: '/sponzori' },
      { label: t('nav.contact'), to: '/kontakt' }
    );
    
    links = baseLinks;
    
    // Add shop if configured
    if (settings?.shop_url) {
      links.push({ label: t('nav.shop'), to: settings.shop_url, external: true } as NavLink);
    }

    // Inject custom pages from settings.custom_nav (label + url + external?)
    const customNav = Array.isArray((settings as any)?.custom_nav) ? ((settings as any).custom_nav as any[]) : [];
    if (customNav.length > 0) {
      const mapped: NavLink[] = customNav.map((it) => ({ label: String(it.label || 'Stránka'), to: String(it.url || '#'), external: Boolean(it.external) }));
      const insertIdx = links.findIndex((n) => n.label === 'Tabulky');
      if (insertIdx >= 0) {
        links = [...links.slice(0, insertIdx + 1), ...mapped, ...links.slice(insertIdx + 1)];
      } else {
        links = [...links, ...mapped];
      }
    }
    
    return links;
  }, [filteredDynamicNavItems, navbarData.navLoading, settings, categoryItems, navbarData.hasTables, navbarData.hasActivities, navbarData.hasPlayers, navbarData.hasArticles, navbarData.hasVideos, navbarData.hasGallery, galleryLabel, i18n.language, t]);

  // Split navigation into visible and overflow for desktop
  const navSplit = useMemo(() => {
    const links = NAV_LINKS;
    let maxVisible = 8;
    const w = windowWidth;
    if (w >= 1600) maxVisible = 10;
    else if (w >= 1400) maxVisible = 9;
    else if (w >= 1200) maxVisible = 8;
    else if (w >= 1100) maxVisible = 7;
    else maxVisible = 6;
    if (links.length <= maxVisible) return { visible: links, overflow: [] as NavLink[] };
    const visibleCount = Math.max(1, maxVisible - 1); // reserve one slot for "More"
    return { visible: links.slice(0, visibleCount), overflow: links.slice(visibleCount) };
  }, [NAV_LINKS, windowWidth]);

  const moreItems = useMemo(() => {
    const out: { label: string; to: string }[] = [];
    navSplit.overflow.forEach((nav) => {
      if (nav.to) out.push({ label: nav.label, to: nav.to });
      if (Array.isArray(nav.items)) {
        nav.items.forEach((it) => out.push({ label: it.label, to: it.to }));
      }
    });
    return out;
  }, [navSplit]);

  // Variant-driven styles for main nav bar
  const headerVariant = variant || 'unified';
  const navBgDefault = useColorModeValue('rgba(255,255,255,0.9)', 'rgba(15,17,21,0.85)');
  const navBgMinimal = useColorModeValue('white', '#0f1115');
  const isTransparent = headerVariant === 'transparent';
  const isMinimal = headerVariant === 'minimal';
  const navBg = isTransparent ? 'transparent' : (isMinimal ? navBgMinimal : navBgDefault);
  const navBackdrop = (isTransparent || isMinimal) ? 'none' : 'saturate(180%) blur(10px)';
  const navBorderBottomWidth = isTransparent ? '0px' : '1px';
  const navBoxShadow = (isTransparent || isMinimal) ? 'none' : (scrolled ? 'sm' : 'none');

  const levelProgress = useMemo(() => {
    const xp = engProfile?.xp ?? 0;
    let lvl = 1;
    let threshold = 100;
    let remaining = xp;
    while (remaining >= threshold && lvl < 200) {
      remaining -= threshold;
      lvl++;
      threshold += 100;
    }
    const pct = Math.max(0, Math.min(100, Math.floor((remaining / threshold) * 100)));
    return { pct };
  }, [engProfile?.xp]);

  return (
    <Box position="sticky" top={0} zIndex={1000}>
      {/* Top bar with socials and quick external links */}
      {(settings?.facebook_url || settings?.instagram_url || settings?.youtube_url || settings?.shop_url) && (
        <Box bg={topBarBg} borderBottomWidth="1px" borderColor="border.subtle" py={1} display={{ base: 'none', md: 'block' }}>
          <Container maxW={containerMaxW} px={fullWidth ? 0 : undefined}>
            <Flex align="center" justify="space-between" gap={2}>
              <HStack spacing={2}>
                {settings?.shop_url && (
                  <Button as="a" href={settings.shop_url} target="_blank" rel="noreferrer" variant="link" size="xs" leftIcon={<FaShoppingBag />}>
                    {t('nav.shop')}
                  </Button>
                )}
              </HStack>
              <HStack spacing={1}>
                {normalizeSocialUrl('facebook', settings?.facebook_url) && (
                  <IconButton as="a" href={normalizeSocialUrl('facebook', settings?.facebook_url) || undefined} target="_blank" rel="noreferrer" aria-label="Facebook" icon={<FaFacebook />} variant="ghost" size="xs" />
                )}
                {normalizeSocialUrl('instagram', settings?.instagram_url) && (
                  <IconButton as="a" href={normalizeSocialUrl('instagram', settings?.instagram_url) || undefined} target="_blank" rel="noreferrer" aria-label="Instagram" icon={<FaInstagram />} variant="ghost" size="xs" />
                )}
                {normalizeSocialUrl('youtube', settings?.youtube_url) && (
                  <IconButton as="a" href={normalizeSocialUrl('youtube', settings?.youtube_url) || undefined} target="_blank" rel="noreferrer" aria-label="YouTube" icon={<FaYoutube />} variant="ghost" size="xs" />
                )}
              </HStack>
            </Flex>
          </Container>
        </Box>
      )}

      {/* Main Nav Bar */}
      <Box
        bg={navBg}
        backdropFilter={navBackdrop}
        borderBottomWidth={navBorderBottomWidth}
        borderColor="border.subtle"
        boxShadow={navBoxShadow}
        transition="box-shadow 0.2s ease, background-color 0.2s ease, backdrop-filter 0.2s ease"
      >        
      <MobileMenu isOpen={isOpen} onClose={onClose} isAdmin={isAdmin} isAuthenticated={isAuthenticated} menuBg={menuBg} dividerColor={dividerColor} settings={settings} categories={navbarData.categories} galleryHref={galleryHref} galleryLabel={galleryLabel} hasTables={navbarData.hasTables} hasActivities={navbarData.hasActivities} hasPlayers={navbarData.hasPlayers} hasArticles={navbarData.hasArticles} hasVideos={navbarData.hasVideos} hasGallery={navbarData.hasGallery} dynamicNavItems={filteredDynamicNavItems || []} navLoading={navbarData.navLoading} t={t} i18n={i18n} handleSimpleLanguageChange={handleSimpleLanguageChange} />
      <Container maxW={containerMaxW} px={fullWidth ? 0 : undefined}>
        <Flex h={16} alignItems="center" justifyContent="space-between">
        <HStack spacing={4} alignItems="center">
          {/* Club Logo only */}
          <HStack as={RouterLink} to="/" spacing={3} align="center">
            {(settings?.club_logo_url || theme.logoUrl) && (
              <Image
                src={assetUrl(settings?.club_logo_url || theme.logoUrl) || settings?.club_logo_url || theme.logoUrl}
                alt={settings?.club_name || theme.name || 'Logo'}
                boxSize={{ base: '36px', md: '40px' }}
                objectFit="contain"
                borderRadius="full"
                borderWidth="2px"
                borderColor="brand.primary"
                style={{
                  padding: (settings?.club_logo_url || theme.logoUrl)?.includes('logoapi.sportcreative.eu') ? '4px' : '0px',
                  boxSizing: 'border-box'
                }}
              />
            )}
          </HStack>
          {/* Desktop navigation with hover dropdowns */}
          <HStack as="nav" spacing={1} display={{ base: 'none', lg: 'flex' }} ml={4}>
            {navSplit.visible.map((nav) => {
              const commonProps = {
                variant: 'ghost' as const,
                size: 'sm' as const,
                px: 3,
                _hover: { bg: hoverBg, transform: 'translateY(-1px)' },
                fontWeight: isPathActive(nav.to) ? '700' : '600',
                color: isPathActive(nav.to) ? activeTextColor : navTextColor,
                bg: isPathActive(nav.to) ? activeBg : 'transparent',
                transition: 'all 0.2s',
              };
              
              // Handle items with dropdown (like Články with categories)
              if (nav.items && nav.items.length > 0) {
                return (
                  <HoverMenu key={nav.label} label={nav.label} to={nav.to || '/blog'} items={nav.items} isActive={isPathActive(nav.to)} />
                );
              }
              
              if (nav.external && nav.to) {
                return (
                  <Button key={nav.label} as="a" href={nav.to} target="_blank" rel="noreferrer" rightIcon={<FaExternalLinkAlt />} {...commonProps}>
                    {nav.label}
                  </Button>
                );
              }
              return (
                <Button key={nav.label} as={RouterLink} to={nav.to || '#'} {...commonProps}>
                  {nav.label}
                </Button>
              );
            })}
            {navSplit.overflow.length > 0 && (
              <HoverMenu key="more" label={t('action.more')} items={moreItems} />
            )}
          </HStack>
        </HStack>

        <Flex alignItems="center">
          {/* Mobile menu button */}
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            onClick={onOpen}
            icon={<HamburgerIcon />}
            aria-label={t('action.open_menu')}
            variant="ghost"
            mr={2}
          />

          {/* Space reserved (socials moved to top bar) */}
          <Box display={{ base: 'none', md: 'flex' }} mr={2} />

          {/* Search button */}
          <Tooltip label={t('action.search')} hasArrow>
            <IconButton
              aria-label={t('action.search')}
              icon={<FaSearch />}
              size="sm"
              mr={2}
              variant="ghost"
              onClick={onSearchOpen}
            />
          </Tooltip>

          {/* Admin edit button */}
          {isAdmin && (
            <Tooltip label={t('action.content_admin')} hasArrow>
              <IconButton
                as={RouterLink}
                to="/admin"
                aria-label={t('action.content_admin')}
                icon={<EditIcon />}
                size="sm"
                mr={2}
                colorScheme="blue"
                variant="ghost"
              />
            </Tooltip>
          )}

          {/* Language switcher - compact dropdown for all users */}
          <LanguageSwitcher />

          {/* Color mode toggle */}
          <ThemeToggle />

          {/* Auth buttons (desktop) */}
          {!isAuthenticated && (
            <>
              <Button
                as={RouterLink}
                to="/register"
                size="sm"
                variant="outline"
                display={{ base: 'none', md: 'inline-flex' }}
                ml={2}
                mr={2}
              >
                {t('auth.register')}
              </Button>
              <Button
                as={RouterLink}
                to="/login"
                size="sm"
                colorScheme="blue"
                display={{ base: 'none', md: 'inline-flex' }}
                mr={2}
              >
                {t('auth.login')}
              </Button>
            </>
          )}

          {isAuthenticated && (
            <Menu>
              <MenuButton
                as={Button}
                rounded="full"
                variant="link"
                cursor="pointer"
                minW={0}
                ml={2}
              >
                <Avatar size="sm" name={user?.name || t('common.user')} src={engProfile?.animated_avatar_url || engProfile?.avatar_url || undefined} />
              </MenuButton>
              <MenuList>
                <MenuItem isDisabled>{`${t('engagement.level')} ${engProfile?.level ?? 1} • ${engProfile?.points ?? 0} ${t('engagement.points')}`}</MenuItem>
                <Box px={3} py={2}>
                  <Text fontSize="xs" color="gray.500">{t('engagement.progress')}</Text>
                  <Progress value={levelProgress.pct} size="xs" colorScheme="blue" borderRadius="full" mt={1} />
                </Box>
                {!isAdmin && (
                  <>
                    <MenuItem onClick={onAchOpen}>{t('engagement.achievements')}</MenuItem>
                    <MenuItem onClick={onRewOpen}>{t('engagement.rewards')}</MenuItem>
                  </>
                )}
                <MenuItem as={RouterLink} to={accountPath}>{t('common.my_account')}</MenuItem>
                <MenuItem onClick={openMyNewsletterPrefs}>{t('newsletter.email_preferences')}</MenuItem>
                {isAdmin && <MenuItem as={RouterLink} to="/admin/nastaveni">{t('admin.page_settings')}</MenuItem>}
                {isAdmin && <MenuItem as={RouterLink} to="/admin">{t('nav.admin')}</MenuItem>}
                <MenuItem onClick={logout}>{t('auth.logout')}</MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
        {/* Close outer Flex */}
        </Flex>
      </Container>

      {/* Search Modal */}
      <Modal isOpen={isSearchOpen} onClose={onSearchClose} size="md" motionPreset="scale">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('search.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitSearch();
              }}
            >
              <VStack spacing={4}>
                <InputGroup size="lg">
                  <InputLeftElement pointerEvents="none">
                    <FaSearchIcon />
                  </InputLeftElement>
                  <Input
                    placeholder={t('search.placeholder')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                  />
                </InputGroup>
                <Button type="submit" colorScheme="blue" size="lg" w="full" leftIcon={<FaSearchIcon />}>
                  {t('search.search_button')}
                </Button>
              </VStack>
            </form>
            <Text fontSize="sm" color="gray.500" mt={4} textAlign="center">
              {t('search.search_hint')}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
      {!isAdmin && (
        <>
          <AchievementsModal isOpen={isAchOpen} onClose={onAchClose} onOpenRewards={onRewOpen} />
          <RewardsModal
            isOpen={isRewOpen}
            onClose={onRewClose}
            availablePoints={engProfile?.points || 0}
            onRedeemed={async () => { try { const p = await getEngagementProfile(); setEngProfile(p); } catch {} }}
          />
        </>
      )}

      </Box>
    </Box>
  );
};

// HoverMenu component for desktop dropdown nav
const HoverMenu = ({ label, items, to, isActive }: { label: string; items: { label: string; to: string }[]; to?: string; isActive?: boolean }) => {
  const menuColorActive = useColorModeValue('brand.primary', 'brand.accent');
  const menuColorInactive = useColorModeValue('gray.700', 'gray.200');
  const menuBgActive = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const menuHoverBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const clearTimers = () => {
    if (openTimer.current) { window.clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
  };

  const handleOpen = () => {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
    if (!open) {
      openTimer.current = window.setTimeout(() => setOpen(true), 60);
    }
  };
  const handleClose = () => {
    if (openTimer.current) { window.clearTimeout(openTimer.current); openTimer.current = null; }
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => clearTimers(), []);

  const hasParentLink = typeof to === 'string' && to.trim().length > 0;
  const isExternalParent = hasParentLink && /^https?:\/\//i.test(to!);

  const onParentClick = (e: React.MouseEvent) => {
    if (!hasParentLink) return;
    if (isExternalParent) {
      window.open(to!, '_blank', 'noopener,noreferrer');
    } else {
      navigate(to!);
    }
  };

  return (
    <Menu isOpen={open} placement="bottom-start" gutter={0} closeOnBlur={false} isLazy>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        variant="ghost"
        size="sm"
        px={3}
        fontWeight={isActive ? '700' : '600'}
        color={isActive ? menuColorActive : menuColorInactive}
        bg={isActive ? menuBgActive : 'transparent'}
        _hover={{ bg: menuHoverBg, transform: 'translateY(-1px)' }}
        transition="all 0.2s"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onClick={onParentClick}
      >
        {label}
      </MenuButton>
      <MenuList onMouseEnter={handleOpen} onMouseLeave={handleClose}>
        {items.map((it) => {
          const isExternal = /^https?:\/\//i.test(it.to);
          if (isExternal) {
            return (
              <MenuItem as="a" href={it.to} key={it.to} target="_blank" rel="noreferrer">
                {it.label}
              </MenuItem>
            );
          }
          return (
            <MenuItem as={RouterLink} to={it.to} key={it.to}>
              {it.label}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export default Navbar;

// Search Modal rendered alongside Navbar content
// Note: We append the modal inside Navbar return to keep code compact
